import { inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Subject } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * Detects user inactivity across the whole app and drives the idle-timeout popup.
 *
 * User activity (mouse, keyboard, scroll, touch) resets the idle timer. After
 * `environment.session.idleTimeoutMs` of no activity, `idleStart$` emits so the
 * UI can warn the user; if they don't respond, the caller signs them out.
 *
 * SSR-safe: all timers and DOM listeners are only wired up in the browser.
 * Listeners run outside Angular so routine mouse/scroll activity doesn't churn
 * change detection — only the actual idle event re-enters the zone.
 */
@Injectable({ providedIn: 'root' })
export class IdleService {
  private platformId = inject(PLATFORM_ID);
  private zone       = inject(NgZone);

  private readonly idleTimeoutMs = environment.session.idleTimeoutMs;

  /** Emits once when the user has been idle long enough to warrant a warning. */
  private idleStartSubject = new Subject<void>();
  idleStart$ = this.idleStartSubject.asObservable();

  private readonly activityEvents = [
    'mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click', 'wheel',
  ];
  private activityHandler = () => this.onActivity();

  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private running = false;
  private warned  = false;
  private lastArm = 0;

  /** Begin watching for inactivity. No-op on the server or if already running. */
  start(): void {
    if (!isPlatformBrowser(this.platformId) || this.running) return;
    this.running = true;
    this.warned  = false;
    this.zone.runOutsideAngular(() => {
      this.activityEvents.forEach(e =>
        window.addEventListener(e, this.activityHandler, { passive: true }));
    });
    this.armTimer();
  }

  /** Stop watching entirely (e.g. on sign-out). */
  stop(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.running = false;
    this.warned  = false;
    this.clearTimer();
    this.activityEvents.forEach(e => window.removeEventListener(e, this.activityHandler));
  }

  /** Dismiss the warning and resume watching from zero ("Stay signed in"). */
  reset(): void {
    if (!isPlatformBrowser(this.platformId) || !this.running) return;
    this.warned = false;
    this.armTimer();
  }

  private onActivity(): void {
    // Ignore activity while the warning is showing — the user must click Stay/Logout.
    if (!this.running || this.warned) return;
    // Throttle re-arming to at most once per second; mousemove/scroll fire rapidly.
    const now = Date.now();
    if (now - this.lastArm < 1000) return;
    this.lastArm = now;
    this.armTimer();
  }

  private armTimer(): void {
    this.clearTimer();
    this.lastArm = Date.now();
    this.idleTimer = setTimeout(() => {
      this.warned = true;
      // Re-enter Angular so the popup component's signals update.
      this.zone.run(() => this.idleStartSubject.next());
    }, this.idleTimeoutMs);
  }

  private clearTimer(): void {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }
}
